import { apiClient } from './api-client';
import type { InspectionWarrantyStatus } from './inspection.service';

export type IntakeStatus = 'RECEIVED' | 'ASSIGNED' | 'IN_INSPECTION' | 'INSPECTED' | 'CLOSED';
export type IntakeAccessoryStatus = 'RECEIVED' | 'MISSING' | 'DAMAGED';
export type IntakeShipmentType = 'COURIER' | 'TRANSPORT' | 'HAND_DELIVERY' | 'PICKUP' | 'OTHER';
export type IntakeOwnershipType = 'CUSTOMER' | 'COMPANY' | 'RENTAL' | 'DEMO' | 'STOCK';

export interface ReceivedConditionFlag {
  present: boolean;
  notes?: string;
}

/** Purely observational structured checklist — persisted to receivedConditionJson. */
export interface ReceivedCondition {
  physicalDamage: ReceivedConditionFlag;
  displayCondition: ReceivedConditionFlag;
  waterDamage: ReceivedConditionFlag;
  brokenSeal: ReceivedConditionFlag;
  missingParts: ReceivedConditionFlag;
  tamperEvidence: ReceivedConditionFlag;
  generalRemarks?: string;
}

export const INTAKE_STATUS_LABELS: Record<IntakeStatus, string> = {
  RECEIVED: 'Received',
  ASSIGNED: 'Assigned',
  IN_INSPECTION: 'In Inspection',
  INSPECTED: 'Inspected',
  CLOSED: 'Closed',
};

export const SHIPMENT_TYPE_LABELS: Record<IntakeShipmentType, string> = {
  COURIER: 'Courier',
  TRANSPORT: 'Transport',
  HAND_DELIVERY: 'Hand Delivery',
  PICKUP: 'Pickup',
  OTHER: 'Other',
};

export const OWNERSHIP_TYPE_LABELS: Record<IntakeOwnershipType, string> = {
  CUSTOMER: 'Customer',
  COMPANY: 'Company',
  RENTAL: 'Rental',
  DEMO: 'Demo',
  STOCK: 'Stock',
};

export interface IntakeAccessoryItem {
  id: string;
  accessoryId: string | null;
  accessoryName: string;
  status: IntakeAccessoryStatus;
  notes: string | null;
}

export interface IntakeAccessoryInput {
  accessoryId?: string;
  accessoryName: string;
  status: IntakeAccessoryStatus;
  notes?: string;
}

export interface ShipmentAttachment {
  key: string;
  url: string;
}

export interface IntakeShipment {
  id: string;
  shipmentType: IntakeShipmentType;
  courierName: string | null;
  trackingNumber: string | null;
  dispatchDate: string | null;
  receivedDate: string | null;
  remarks: string | null;
  attachments: ShipmentAttachment[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Bill-To / Ship-To.
 *
 * The client sends SELECTIONS ONLY — never an address, never a GST number.
 * The server resolves the selection against customers / customer_locations and
 * freezes a snapshot. Snapshots are read-only here, forever: they are the
 * historical record printed on quotations, invoices and dispatch notes.
 */
export type AddressSource = 'CUSTOMER_DEFAULT' | 'LOCATION';

export interface AddressSelection {
  source: AddressSource;
  /** Required iff source === 'LOCATION'. */
  locationId?: string;
}

interface BaseAddressSnapshot {
  label: string;
  line1: string;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  phone: string | null;
  capturedAt: string;
}

/** Carries the customer's GST, frozen at capture. */
export interface BillToAddressSnapshot extends BaseAddressSnapshot {
  gstNumber: string | null;
}

/** No gstNumber — a shipping destination has no tax identity. */
export type ShipToAddressSnapshot = BaseAddressSnapshot;

export interface UpdateIntakeAddressesPayload {
  billTo?: AddressSelection;
  shipTo?: AddressSelection;
}

/**
 * The customer's REGISTERED address — master data, read-only, never carries GST.
 *
 * Intentionally a different shape from BillToAddressSnapshot (no `capturedAt`,
 * no `gstNumber`, no `label`) so the two cannot be fed to the same renderer.
 * The Bill-To snapshot is the commercial record that prints on an invoice; this
 * is just where the customer is currently registered.
 */
export interface CustomerRegisteredAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface EquipmentIntake {
  id: string;
  tenantId: string;
  intakeNumber: string;
  intakeCode: string;
  qrCodeValue: string;
  intakeDate: string;
  customerId: string | null;
  customerLocationId: string | null;
  customerContactId: string | null;
  equipmentId: string;
  ownershipTypeAtIntake: IntakeOwnershipType;
  receivedByUserId: string | null;
  receivedAt: string;
  complaintDescription: string;
  conditionOnReceipt: string | null;
  receivedConditionJson: ReceivedCondition | null;
  remarks: string | null;
  accessories: IntakeAccessoryItem[];
  photoUrls: string[];
  shipments: IntakeShipment[];
  assignedEngineerId: string | null;
  assignedByUserId: string | null;
  assignedAt: string | null;
  status: IntakeStatus;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Enriched
  customerName: string | null;
  /**
   * The customer's LEGAL organization name — what the list and the intake
   * preview show. `customerName` is the working display name and is the
   * fallback when a customer record has no legal name recorded.
   *
   * Not billing data: no GST, no address. Returned to every intake:view holder,
   * unlike the Bill-To / Ship-To snapshots.
   */
  customerOrganizationName: string | null;
  /** Customer master-data reference code. Shown in the intake preview. */
  customerCode: string | null;
  customerLocationName: string | null;
  /**
   * D-106 - the equipment's CURRENT workflow stage/group, derived by D-102 and
   * resolved server-side for the page. LIST-ONLY: absent from detail reads,
   * hence optional. The browser never computes these.
   */
  workflowStage?: string | null;
  workflowGroup?: string | null;

  // ── Detail-only ────────────────────────────────────────────────────────────
  // Present on `get(id)` and on every single-intake mutation response; ABSENT
  // from `list()`, which runs a deliberately lean query. `undefined` means "not
  // a detail read", `null` means "no value" — the preview always reads a detail
  // response, so it can rely on these.
  //
  // None of it is billing data. The registered address is customer master data
  // and carries no GST; the invoiced address is the frozen billToAddress
  // snapshot above, which stays behind intake:billing:view.
  customerRegisteredAddress?: CustomerRegisteredAddress | null;
  customerContactEmail?: string | null;
  customerContactPhone?: string | null;
  equipmentModelNumber?: string | null;
  equipmentBrandName?: string | null;
  equipmentCategoryName?: string | null;
  /** The equipment's most recent Receiving Lot membership, if any. */
  receivingLotId?: string | null;
  receivingLotNumber?: string | null;
  customerContactName: string | null;
  receivedByName: string | null;
  equipmentName: string;
  equipmentAssetNumber: string;
  equipmentSerialNumber: string;
  assignedEngineerName: string | null;
  assignedByName: string | null;
  // ── Bill-To / Ship-To ──────────────────────────────────────────────────────
  // PERMISSION-GATED by intake:billing:view on the server. `undefined` means
  // "this user may not see billing" (SE / MSE / Commercial); `null` means "this
  // intake has no snapshot" (created before the feature). Do not collapse the
  // two — the UI renders a different empty state for each.
  billToAddress?: BillToAddressSnapshot | null;
  billToLocationId?: string | null;
  shipToAddress?: ShipToAddressSnapshot | null;
  shipToLocationId?: string | null;
}

export interface EquipmentIntakeListResponse {
  items: EquipmentIntake[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateShipmentPayload {
  shipmentType: IntakeShipmentType;
  courierName?: string;
  trackingNumber?: string;
  dispatchDate?: string;
  receivedDate?: string;
  remarks?: string;
}

export interface UpsertShipmentPayload extends CreateShipmentPayload {
  id?: string;
}

export interface CreateEquipmentIntakePayload {
  intakeDate: string;
  customerId?: string;
  customerLocationId?: string;
  customerContactId?: string;
  equipmentId: string;
  ownershipTypeAtIntake?: IntakeOwnershipType;
  complaintDescription: string;
  conditionOnReceipt?: string;
  receivedConditionJson?: ReceivedCondition;
  remarks?: string;
  accessories?: IntakeAccessoryInput[];
  assignedEngineerId?: string;
  shipments?: CreateShipmentPayload[];
  /** Passed through to the auto-created Inspection — not stored on EquipmentIntake itself. */
  priorityId?: string;
  warrantyStatus?: InspectionWarrantyStatus;
  warrantyTypeId?: string;
  /** Selections only. The server resolves and freezes the addresses. */
  billTo?: AddressSelection;
  shipTo?: AddressSelection;
}

export interface UpdateEquipmentIntakePayload {
  // intakeDate, customerId and equipmentId are CREATE-ONLY. They are absent
  // from the server's UpdateEquipmentIntakeDto, and with forbidNonWhitelisted a
  // request naming them is rejected outright — so they are absent here too.
  customerLocationId?: string;
  customerContactId?: string;
  complaintDescription?: string;
  conditionOnReceipt?: string;
  receivedConditionJson?: ReceivedCondition;
  remarks?: string;
  accessories?: IntakeAccessoryInput[];
  assignedEngineerId?: string;
  status?: IntakeStatus;
  shipments?: UpsertShipmentPayload[];
}

export const equipmentIntakeService = {
  list: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
    customerId?: string;
    equipmentId?: string;
    status?: IntakeStatus;
  }): Promise<EquipmentIntakeListResponse> =>
    apiClient.get<EquipmentIntakeListResponse>('/equipment-intake', { params }).then((r) => r.data),

  get: (id: string): Promise<EquipmentIntake> =>
    apiClient.get<EquipmentIntake>(`/equipment-intake/${id}`).then((r) => r.data),

  create: (payload: CreateEquipmentIntakePayload): Promise<EquipmentIntake> =>
    apiClient.post<EquipmentIntake>('/equipment-intake', payload).then((r) => r.data),

  update: (id: string, payload: UpdateEquipmentIntakePayload): Promise<EquipmentIntake> =>
    apiClient.patch<EquipmentIntake>(`/equipment-intake/${id}`, payload).then((r) => r.data),

  /**
   * The ONLY way to change Bill-To / Ship-To after creation. Deliberately not
   * part of `update` — routing address changes through the general update would
   * make immutability a convention rather than a guarantee.
   *
   * Never call this in response to a customer edit, branch edit, GST change,
   * status change or Received-From correction. Automatic synchronization is
   * forbidden; the UI may suggest, but only a person may act.
   */
  updateAddresses: (id: string, payload: UpdateIntakeAddressesPayload): Promise<EquipmentIntake> =>
    apiClient
      .patch<EquipmentIntake>(`/equipment-intake/${id}/addresses`, payload)
      .then((r) => r.data),

  remove: (id: string): Promise<void> =>
    apiClient.delete(`/equipment-intake/${id}`).then(() => undefined),

  uploadPhoto: (id: string, file: File): Promise<EquipmentIntake> => {
    const form = new FormData();
    form.append('photo', file, file.name);
    return apiClient
      .post<EquipmentIntake>(`/equipment-intake/${id}/photos`, form)
      .then((r) => r.data);
  },

  deletePhoto: (id: string, photoIndex: number): Promise<EquipmentIntake> =>
    apiClient
      .delete<EquipmentIntake>(`/equipment-intake/${id}/photos/${photoIndex}`)
      .then((r) => r.data),

  uploadShipmentAttachment: (
    id: string,
    shipmentId: string,
    file: File,
  ): Promise<EquipmentIntake> => {
    const form = new FormData();
    form.append('file', file, file.name);
    return apiClient
      .post<EquipmentIntake>(`/equipment-intake/${id}/shipments/${shipmentId}/attachments`, form)
      .then((r) => r.data);
  },

  deleteShipmentAttachment: (
    id: string,
    shipmentId: string,
    attachmentIndex: number,
  ): Promise<EquipmentIntake> =>
    apiClient
      .delete<EquipmentIntake>(
        `/equipment-intake/${id}/shipments/${shipmentId}/attachments/${attachmentIndex}`,
      )
      .then((r) => r.data),
};
